import { ChangeEvent, ReactElement, ReactNode, useMemo } from 'react';

import classes from './FloorMap.module.scss';
import { FloorMapBackground } from './FloorMapBackground';
import { FloorMapFallback } from './FloorMapFallback';
import { FloorMapLoading } from './FloorMapLoading';
import { useFloorMap } from './FloorMapProvider';
import { FloorMapUploader } from './FloorMapUploader';
import { Point, RenderBookingObjectParams } from './types';

export interface FloorMapProps<T> {
  supportsDragging?: (id: string) => boolean;
  supportsAdd?: boolean;
  supportsUpload?: boolean;
  showDisabled?: boolean;
  loading: boolean;
  bookingObjects: Map<string, T>;
  onUpload?: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  onClickOnMap?: (point: Point) => void;
  onCursorMove?: (point: Point) => void;
  onCursorLeave?: () => void;
  onClickOnBookingObject?: (id: string) => void;
  onHoverOnBookingObject?: (id: string) => void;
  onDragBookingObject?: (point: Point) => void;
  onMoveMap?: () => void;
  footer?: ReactNode;
  renderBookingObject: (params: RenderBookingObjectParams<T>) => ReactNode;
  touchDevice?: boolean;
  fullscreenIsSupported?: boolean;
  backgroundLoadingError?: string;
}

export const FloorMap = <T,>({
  supportsDragging,
  supportsAdd,
  supportsUpload,
  showDisabled,
  bookingObjects,
  onUpload,
  onClickOnMap,
  onCursorMove,
  onCursorLeave,
  onClickOnBookingObject,
  onHoverOnBookingObject,
  onDragBookingObject,
  onMoveMap,
  loading,
  footer,
  renderBookingObject,
  touchDevice,
  fullscreenIsSupported,
  backgroundLoadingError,
}: FloorMapProps<T>): ReactElement => {
  const { backgroundData, floor, floorLoading } = useFloorMap();

  const content = useMemo((): ReactNode => {
    if (loading || floorLoading) return <FloorMapLoading />;

    if (floor) {
      if (!floor.inheritedEnabled && !showDisabled)
        return (
          <FloorMapFallback>
            {floor.inheritedDisabledReason ||
              'Ведутся технические работы, этаж скоро будет доступен'}
          </FloorMapFallback>
        );

      if (floor.planUnderlayLoaded && !backgroundData)
        return <FloorMapLoading />;

      if (!backgroundData) {
        if (supportsUpload && onUpload)
          return (
            <FloorMapUploader
              onUpload={onUpload}
              error={backgroundLoadingError}
            />
          );

        return (
          <FloorMapFallback>
            Обратитесь к администратору для добавления плана этажа
          </FloorMapFallback>
        );
      }

      return (
        <FloorMapBackground
          supportsAdd={supportsAdd}
          supportsDragging={supportsDragging}
          backgroundData={backgroundData}
          bookingObjects={bookingObjects}
          onClickOnMap={onClickOnMap}
          onCursorMove={onCursorMove}
          onCursorLeave={onCursorLeave}
          onClickOnBookingObject={onClickOnBookingObject}
          onHoverOnBookingObject={onHoverOnBookingObject}
          onDragBookingObject={onDragBookingObject}
          onMoveMap={onMoveMap}
          renderBookingObject={renderBookingObject}
          touchDevice={touchDevice}
          fullscreenIsSupported={fullscreenIsSupported}
        />
      );
    }

    return (
      <FloorMapFallback>Тут отобразится план выбранного этажа</FloorMapFallback>
    );
  }, [
    backgroundData,
    backgroundLoadingError,
    bookingObjects,
    floor,
    floorLoading,
    fullscreenIsSupported,
    loading,
    onClickOnBookingObject,
    onClickOnMap,
    onCursorLeave,
    onCursorMove,
    onDragBookingObject,
    onHoverOnBookingObject,
    onMoveMap,
    onUpload,
    renderBookingObject,
    showDisabled,
    supportsAdd,
    supportsDragging,
    supportsUpload,
    touchDevice,
  ]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.content} data-testid='floorMap'>
        {content}
      </div>
      {footer && <div className={classes.footer}>{footer}</div>}
    </div>
  );
};
